import {
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {EventInterface} from '../types/event/event.interface';
import {TopicInterface} from '../types/topic/topic.interface';
import {EventRouteInterface} from '../types/event/event-route.interface';
import {EventsResponse} from '../types/event/events-response.interface';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  currentEvent = signal<EventInterface>({} as EventInterface);
  startEventTopics = signal<TopicInterface[]>([]);
  activeTopic = signal<TopicInterface>({} as TopicInterface);
  totalSavedCo2 = signal<string>('');
  eventRoute = signal<EventRouteInterface>({} as EventRouteInterface);
  isPdfGenerates = signal<boolean>(false);

  constructor() {
  }

  private httpClient = inject(HttpClient);

  getEventById(eventId: number): Observable<EventInterface> {
    return this.httpClient
      .get<EventInterface>('event/get', {
        params: {eventId: eventId},
      })
      .pipe(tap((event) => this.totalSavedCo2.set(event.totalSavedCO2)));
  }

  getEventByMemberId(memberId = 0, search: string = "", page: number = 1): Observable<EventsResponse> {
    return this.httpClient.get<EventsResponse>('event/get', {
      params: {memberId: memberId, search, page},
    });
  }

  getReport(eventId: number): Observable<any> {
    return this.httpClient.get(`report`, {
      params: {id: eventId}
    });
  }

  downloadMasterReport(): Observable<any> {
    return this.httpClient.get(`report`, {
      params: {all: true, resave: false},
      responseType: 'blob',
    });
  }

  recalculateAllReports(): Observable<any> {
    return this.httpClient.get(`report`, {
      params: {all: true, resave: true},
      responseType: 'blob',
    });
  }

  performDownload(response: any, fileName: string) {
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName; // Name file with event ID
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    window.URL.revokeObjectURL(url);
  }

  recalculate(eventId: number): Observable<EventInterface> {
    return this.httpClient.get<EventInterface>(`event/recalculate`, {
      params: {id: eventId},
    });
  }

  saveEvent(): Observable<EventInterface> {
    const event = this.currentEvent();
    return this.httpClient
      .post<EventInterface>('event/save', {
        name: event.name,
        json: JSON.stringify(event),
      })
      .pipe(tap((event) => this.currentEvent.set(event)));
  }

  updateEvent(id: number): Observable<EventInterface> {
    const event = this.currentEvent();
    return this.httpClient
      .post<EventInterface>('event/save', {
        id,
        name: event.name,
        json: JSON.stringify(event),
      })
      .pipe(tap((event) => this.currentEvent.set(event)));
  }

  deleteEvent(eventId: number): Observable<EventInterface> {
    return this.httpClient.delete<EventInterface>('event/delete', {
      params: {id: eventId},
    });
  }

  getStartEventConfig(): Observable<EventInterface> {
    return this.httpClient
      .get<EventInterface>('guide')
      .pipe()
      .pipe(tap((event) => this.totalSavedCo2.set(event.totalSavedCO2)));
  }

  createPostEvent(id: number): Observable<EventInterface> {
    return this.httpClient
      .get<EventInterface>('event/createpostevent', {
        params: {id},
      })
      .pipe(tap((event) => this.totalSavedCo2.set(event.totalSavedCO2)));
  }

  getSkippedQuestionData(event: EventInterface): EventRouteInterface | null {
    const {id: eventId, topics} = event;

    for (const topic of topics) {
      const skippedQuestion = topic.questions.find(
        (q) => q.isSkipped && !q.isOptional
      );
      if (skippedQuestion) {
        return {
          eventId,
          eventTopic: String(topic.type).toLowerCase(),
          currentQuestion: skippedQuestion.id,
        } as EventRouteInterface;
      }
    }

    return null;
  }
}
